import {EntityType, getEntityLink, getEntityViewPermission} from "@ots-frontend/ots-data";
import {createContext, PropsWithChildren, ReactNode, useContext} from "react";
import {Link, useInRouterContext} from "react-router-dom";
import {AuthContext} from "../app/AuthProvider";
import {otsLinkClassName} from "./OtsLink";

/**
 * How entity links behave in this part of the app. By default they route to the entity's profile page
 * (`/sites/profile?id=…`). Hosts without those routes, such as GWT, can hand clicks back instead, or
 * turn links off so names render as plain text.
 */
export type OtsEntityLinkSettings = {
  /** Called instead of navigating, for hosts that open profiles themselves. */
  onOpen?: (entityType: string, id: string) => void;
  disabled?: boolean;
};

const OtsEntityLinkContext = createContext<OtsEntityLinkSettings>({});

export const OtsEntityLinkProvider = ({children, ...settings}: PropsWithChildren<OtsEntityLinkSettings>) => (
  <OtsEntityLinkContext.Provider value={settings}>{children}</OtsEntityLinkContext.Provider>
);

export type OtsEntityLinkProps = {
  entityType: EntityType | string;
  id?: string;
  /** What the link says; falls back to `empty` when missing. */
  name?: ReactNode;
  empty?: ReactNode;
  className?: string;
};

/**
 * Names a related entity and links to its profile when the viewer may see it and the app has a profile
 * route for its type; otherwise renders the name as plain text.
 */
export const OtsEntityLink = ({entityType, id, name, empty = "—", className}: OtsEntityLinkProps) => {
  const settings = useContext(OtsEntityLinkContext);
  const auth = useContext(AuthContext);
  const inRouter = useInRouterContext();

  const label = name ?? empty;
  if (!id || name == null || settings.disabled) return <span className={className}>{label}</span>;

  const permission = getEntityViewPermission(entityType);
  // no auth context (stories, previews) means there is nothing to check against
  const allowed = !permission || !auth || auth.hasAccess(permission);
  const href = getEntityLink(entityType as EntityType, id);
  if (!allowed || (!href && !settings.onOpen)) return <span className={className}>{label}</span>;

  const classes = [otsLinkClassName, className].filter(Boolean).join(" ");

  if (settings.onOpen) {
    return (
      <button
        type="button"
        className={`${classes} cursor-pointer bg-transparent p-0 text-left`}
        onClick={() => settings.onOpen?.(entityType, id)}>
        {label}
      </button>
    );
  }

  return inRouter
    ? <Link to={href as string} className={classes}>{label}</Link>
    : <a href={href} className={classes}>{label}</a>;
};
